import get from 'lodash/get';
import isNull from 'lodash/isNull';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import transform from 'lodash/transform';
import { Dictionary } from 'lodash';
import mapKeys from 'lodash/mapKeys';
import camelCase from 'lodash/camelCase';

/**
 * This function will convert a given value to undefined if it's equal to empty string or nullable.
 * Dependencies: lodash/isNull
 *
 * @param val any
 */
export const toUndefined = val => val === '' || isNull(val) ? undefined : val;

/**
 * This function gets two objects, the second object the one to be compared.
 * And returns a new object which will include a deep diff between two objects.
 * Dependencies: lodash/transform, lodash/isObject, lodash/isEqual
 */
// tslint:disable-next-line:no-any
export const difference = (object, base): Dictionary<any> => {
  const changes = (_object, _base) => {
    return transform(_object, (result, value, key) => {
      const baseItem = toUndefined(_base[key]);
      const valueItem = toUndefined(value);
      if (!isEqual(valueItem, baseItem)) {
        result[key] = (isObject(valueItem) && isObject(baseItem)) ? changes(valueItem, baseItem) : valueItem;
      }
    });
  };

  return changes(object, base);
};

/**
 * Make an object clone from base obj using key map array.
 * Dependencies: lodash/get
 *
 * @param obj Object
 * @param keys Array<string>
 */
export const takeSnapshot = (obj, keys) => {
  const res = {};
  keys.forEach(key => {
    res[key.split('.')[0]] = get(obj, key);
  });

  return res;
};

/**
 * Return deep value of object
 * Usage:
 * getDeepValue({ a: { b: { c: 1 } } }, 'a.b.c'); // => 1
 */
export const getDeepValue = (obj, path: string): string => {
  for (let i = 0, p = path.split('.'), len = p.length; i < len; i++) {
    if (obj.hasOwnProperty(p[i])) {
      obj = obj[p[i]];
    }
  }

  return obj || '';
};

/**
 * Return the same object but with camelCase keys instead.
 * Usage:
 * makeKeysCamelCase({ not_camel: '1', 'definitely-not-camel': '2' }); // { notCamel: '1', definitelyNotCamel: '2' }
 */
export const makeKeysCamelCase = <T = Dictionary<string>>(obj): T => mapKeys(obj, (value, key) => camelCase(key)) as T;

/**
 * Filters an object to keep only the specified keys.
 *
 * @template T - The type of the input object.
 * @template K - An array of keys that the object should be filtered by.
 * that match the specified keys from the provided array.
 */
export const keepOnly = <T extends object, K extends Array<keyof T>>(
  obj: T,
  keys: K
): Pick<T, K[number]> => {
  const result = {} as Pick<T, K[number]>;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key];
    }
  }

  return result;
};
